"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Logger = void 0;
const default_1 = require("./constants/default");
const logger_1 = require("./types/logger");
const LogLevelNames = Object.fromEntries(Object.entries(logger_1.LogLevels).map(([key, value]) => [value, key]));
/**
 * A utility class for logging messages with support for multiple log levels, environments, destinations, and custom formatting.
 *
 * @template AllowedEnvironments - A string union type representing the allowed environments (e.g., 'development', 'production').
 */
class Logger {
    constructor({ destinations = [], environmentManager, formatter, loggerOptions, logLevel = Logger.DEFAULT_LOG_LEVEL, storage, }) {
        this.environmentLogLevels = {};
        this.options = Object.assign({}, Logger.DEFAULT_OPTIONS, loggerOptions);
        this.options.colors = Object.assign({}, this.options.colors, loggerOptions === null || loggerOptions === void 0 ? void 0 : loggerOptions.colors);
        this.currentLogLevel = logLevel;
        this.currentPrefix = '';
        this.destinations = destinations;
        this.formatter = formatter;
        this.storage = storage;
        this.environmentManager = environmentManager;
    }
    /**
     * Sets the log level for a specific environment.
     *
     * @param env - The environment name.
     * @param level - The log level to set for the environment.
     */
    setLogLevelForEnvironment(env, level) {
        this.environmentLogLevels[env] = level;
    }
    /**
     * Gets the log level for the current environment.
     *
     * @returns The log level for the current environment.
     */
    getLogLevelForEnvironment() {
        if (!this.environmentManager) {
            return this.currentLogLevel;
        }
        const currentEnv = this.environmentManager.getCurrentEnv();
        return this.environmentLogLevels[currentEnv] || this.currentLogLevel;
    }
    /**
     * Checks if logging is allowed in the current environment.
     *
     * @returns `true` if logging is allowed, otherwise `false`.
     */
    isAllowedEnvironment() {
        if (!this.environmentManager) {
            return true;
        }
        const currentEnv = this.environmentManager.getCurrentEnv();
        return Object.keys(this.environmentLogLevels).includes(currentEnv);
    }
    /**
     * Checks if a log message should be sent based on its level and environment.
     *
     * @param logLevel - The log level of the message.
     * @returns `true` if the message should be logged, otherwise `false`.
     */
    hasLevelToSendLog(logLevel) {
        const envLogLevel = this.getLogLevelForEnvironment();
        return this.isAllowedEnvironment() && envLogLevel <= logLevel;
    }
    /**
     * Formats a log message.
     *
     * @param level - The log level of the message.
     * @param message - The message to format.
     * @returns The formatted log message.
     */
    formatMessage(level, message) {
        var _a;
        const levelName = LogLevelNames[level] || 'Unknown';
        const currentEnv = ((_a = this.environmentManager) === null || _a === void 0 ? void 0 : _a.getCurrentEnv()) || 'default';
        if (this.formatter) {
            return this.formatter(level, message, currentEnv);
        }
        return `[${currentEnv}] [${levelName}]: ${message}`;
    }
    /**
     * Sends a log message to all configured destinations.
     *
     * @param level - The log level of the message.
     * @param message - The message to send.
     */
    sendToDestinations(level, message) {
        var _a;
        const currentEnv = ((_a = this.environmentManager) === null || _a === void 0 ? void 0 : _a.getCurrentEnv()) || 'default';
        this.destinations.forEach(destination => destination.log(level, message, currentEnv));
    }
    /**
     * Saves a log message to the configured storage.
     *
     * @param level - The log level of the message.
     * @param message - The message to save.
     */
    saveToStorage(level, message) {
        if (this.storage) {
            this.storage.saveLog(level, message, new Date());
        }
    }
    /**
     * Adds prefixes and colors to a log message.
     *
     * @param messages - The log messages.
     * @param color - The color to apply to the message.
     * @returns The modified log messages.
     */
    addPrefixAndColor(messages, color) {
        const prefixes = this.resolvePrefixes();
        if (prefixes) {
            messages.splice(0, 0, prefixes);
            if (this.options.withColors && color && color !== '') {
                messages[0] = `%c${messages[0]}`;
                messages.splice(1, 0, color);
            }
        }
        else if (this.options.withColors && color && color !== '') {
            messages[0] = `%c${messages[0]}`;
            messages.splice(1, 0, color);
        }
        return messages;
    }
    /**
     * Resolves and constructs a string of prefixes to be added to log messages.
     *
     * This method combines the current prefix and any additional prefixes specified in the logger options.
     * The prefixes are joined using the configured separators and returned as a single formatted string.
     *
     * @returns A formatted string containing the resolved prefixes.
     * If no prefixes are defined, an empty string is returned.
     *
     * ### Example
     * Given the following configuration:
     * - `currentPrefix = "App"`
     * - `options.otherPrefixes = ["Module", "Component"]`
     * - `options.separatorIn = "["`
     * - `options.separator = " > "`
     * - `options.separatorOut = "]"`
     *
     * The result would be: `[Component > Module > App]`
     *
     * ### Behavior
     * 1. If `currentPrefix` is defined and not empty, it is added to the list of prefixes.
     * 2. If `options.otherPrefixes` is defined and contains values, they are appended to the list of prefixes.
     * 3. The prefixes are reversed, joined using `options.separator`, and wrapped with `options.separatorIn` and `options.separatorOut`.
     * 4. If no prefixes are defined, an empty string is returned.
     */
    resolvePrefixes() {
        let r = [];
        let result = '';
        if (this.currentPrefix && this.currentPrefix !== '') {
            r.push(this.currentPrefix);
        }
        if (this.options.otherPrefixes && this.options.otherPrefixes.length > 0) {
            r = [...r, ...this.options.otherPrefixes];
        }
        if (r.length > 0) {
            result = `${this.options.separatorIn}${r.reverse().join(this.options.separator)}${this.options.separatorOut}`;
        }
        return result;
    }
    /**
     * Logs a message at the "Log" level.
     *
     * @param messages - An array of messages to log.
     * The messages are formatted, saved to storage, sent to destinations, and logged to the console with a prefix and color.
     */
    log({ messages }) {
        if (this.hasLevelToSendLog(logger_1.LogLevels.Log) && messages.length > 0) {
            const formattedMessage = this.formatMessage(logger_1.LogLevels.Log, messages.join(' '));
            this.saveToStorage(logger_1.LogLevels.Log, formattedMessage);
            this.sendToDestinations(logger_1.LogLevels.Log, formattedMessage);
            window.console.log(...this.addPrefixAndColor(messages, this.options.colors.log));
        }
    }
    /**
     * Logs a message at the "Info" level.
     *
     * @param messages - An array of messages to log.
     * The messages are formatted, saved to storage, sent to destinations, and logged to the console with a prefix and color.
     */
    info({ messages }) {
        if (this.hasLevelToSendLog(logger_1.LogLevels.Info) && messages.length > 0) {
            const formattedMessage = this.formatMessage(logger_1.LogLevels.Info, messages.join(' '));
            this.saveToStorage(logger_1.LogLevels.Info, formattedMessage);
            this.sendToDestinations(logger_1.LogLevels.Info, formattedMessage);
            window.console.info(...this.addPrefixAndColor([formattedMessage], this.options.colors.info));
        }
    }
    /**
     * Logs a message at the "Warn" level.
     *
     * @param messages - An array of messages to log.
     * The messages are formatted, saved to storage, sent to destinations, and logged to the console with a prefix and color.
     */
    warn({ messages }) {
        if (this.hasLevelToSendLog(logger_1.LogLevels.Warn) && messages.length > 0) {
            const formattedMessage = this.formatMessage(logger_1.LogLevels.Warn, messages.join(' '));
            this.saveToStorage(logger_1.LogLevels.Warn, formattedMessage);
            this.sendToDestinations(logger_1.LogLevels.Warn, formattedMessage);
            window.console.warn(...this.addPrefixAndColor([formattedMessage], this.options.colors.warn));
        }
    }
    /**
     * Logs an error message at the "Error" level.
     *
     * @param error - An optional `Error` or `Event` object to log.
     * If an `Error` is provided, its message and stack trace are logged.
     * If an `Event` is provided, its type and target are logged.
     * @param messages - An optional array of additional messages to log.
     * The error and messages are formatted, saved to storage, sent to destinations, and logged to the console with a prefix and color.
     */
    error({ error, messages }) {
        if (this.hasLevelToSendLog(logger_1.LogLevels.Error)) {
            let errorMessage;
            if (error instanceof Event) {
                errorMessage = `Event type: ${error.type}, Target: ${error.target}`;
            }
            else if (error) {
                errorMessage = `${error.message}\n${error.stack}`;
            }
            else {
                errorMessage = 'Unknown error occurred';
            }
            const combinedMessage = [errorMessage, ...(messages || [])].join(' | ');
            const formattedMessage = this.formatMessage(logger_1.LogLevels.Error, combinedMessage);
            this.saveToStorage(logger_1.LogLevels.Error, formattedMessage);
            this.sendToDestinations(logger_1.LogLevels.Error, formattedMessage);
            window.console.error(...this.addPrefixAndColor([formattedMessage], this.options.colors.error));
        }
    }
    /**
     * Logs a message at the "Trace" level.
     *
     * @param messages - An array of messages to log.
     * The messages are formatted, saved to storage, sent to destinations, and logged to the console with a prefix and color.
     */
    trace({ messages }) {
        if (this.hasLevelToSendLog(logger_1.LogLevels.Trace) && messages.length > 0) {
            const formattedMessage = this.formatMessage(logger_1.LogLevels.Trace, messages.join(' '));
            this.saveToStorage(logger_1.LogLevels.Trace, formattedMessage);
            this.sendToDestinations(logger_1.LogLevels.Trace, formattedMessage);
            window.console.trace(...this.addPrefixAndColor([formattedMessage], this.options.colors.trace));
        }
    }
    /**
     * Starts a new console group with the provided messages.
     *
     * @param messages - An array of messages to include in the group.
     * The messages are logged to the console with a prefix and color.
     */
    group({ messages }) {
        if (this.hasLevelToSendLog(logger_1.LogLevels.Log) && messages.length > 0) {
            window.console.group(...this.addPrefixAndColor(messages, this.options.colors.log));
        }
    }
    /**
     * Starts a new collapsed console group with the provided messages.
     *
     * @param messages - An array of messages to include in the collapsed group.
     * The messages are logged to the console with a prefix and color.
     */
    groupCollapsed({ messages }) {
        if (this.hasLevelToSendLog(logger_1.LogLevels.Log) && messages.length > 0) {
            window.console.groupCollapsed(...this.addPrefixAndColor(messages, this.options.colors.log));
        }
    }
    /**
     * Ends the current console group.
     */
    groupEnd() {
        window.console.groupEnd();
    }
}
exports.Logger = Logger;
Logger.DEFAULT_LOG_LEVEL = logger_1.LogLevels.Warn;
Logger.DEFAULT_OPTIONS = default_1.loggerDefaultOptions;
//# sourceMappingURL=logger.js.map